import { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { authFetch } from './utils/api';
import { useThemeColors } from './utils/useThemeColors';
import { useCompany } from './contexts/CompanyContext';
import { formatDateTime } from './utils/time';
import LogoHeader from './templates/LogoHeader';
import MailerSettingsTab from './SettingsConfig/MailerSettingsTab';
import DealerProductsPanel from './DealerProductsPanel';
import './quotes_base.css';

const Dealers = () => {
    const [dealers, setDealers] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [brandingDealer, setBrandingDealer] = useState(null); // dealer being branded (replaces users panel)
    const [mailerDealer, setMailerDealer] = useState(null);     // dealer whose mailer settings are being edited
    const [productsDealer, setProductsDealer] = useState(null); // dealer whose allowed products are being edited
    const [loadingDealers, setLoadingDealers] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const tc = useThemeColors();
    const { companyData } = useCompany();
    const tz = companyData?.timezone;

    useEffect(() => {
        authFetch('/dealers/')
            .then(data => setDealers(data))
            .catch(err => console.error('Failed to load dealers', err))
            .finally(() => setLoadingDealers(false));
    }, []);

    const handleDealerSelect = async (params) => {
        const dealer = params.row;
        setSelectedDealer(dealer);
        setBrandingDealer(null);
        setMailerDealer(null);
        setProductsDealer(null);
        setLoadingUsers(true);
        try {
            const data = await authFetch(`/dealers/${dealer.id}/users`);
            // For the synthetic "Default" dealer (placeholder for owner-class users),
            // show only users whose role is exactly "owner".
            const isDefault = (dealer.code || '').toLowerCase() === 'default';
            const filtered = isDefault
                ? (data || []).filter(u => (u.role_name || '').toLowerCase() === 'owner')
                : data;
            setUsers(filtered);
        } catch (err) {
            console.error('Failed to load dealer users', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const openBranding = (dealer) => {
        setSelectedDealer(dealer);
        setBrandingDealer(dealer);
        setMailerDealer(null);
        setProductsDealer(null);
    };

    const openMailer = (dealer) => {
        setSelectedDealer(dealer);
        setMailerDealer(dealer);
        setBrandingDealer(null);
        setProductsDealer(null);
    };

    const openProducts = (dealer) => {
        setSelectedDealer(dealer);
        setProductsDealer(dealer);
        setBrandingDealer(null);
        setMailerDealer(null);
    };

    const editButtonStyle = {
        padding: '2px 10px',
        fontSize: '12px',
        cursor: 'pointer',
        background: tc.buttonBg,
        color: tc.buttonText,
        border: `1px solid ${tc.buttonBorder || '#ccc'}`,
        borderRadius: '4px',
    };

    const dealerColumns = [
        { field: 'id',        headerName: 'ID',      width: 70 },
        { field: 'name',      headerName: 'Dealer',  flex: 1 },
        { field: 'code',      headerName: 'Code',    width: 150 },
        { field: 'is_active', headerName: 'Active',  width: 100,
          renderCell: (p) => p.value ? 'Yes' : 'No' },
        {
          field: 'branding', headerName: 'Branding', width: 110, sortable: false, filterable: false,
          renderCell: (p) => (
            <button
              onClick={(e) => { e.stopPropagation(); openBranding(p.row); }}
              style={editButtonStyle}
            >
              Edit
            </button>
          )
        },
        {
          field: 'mailer', headerName: 'Mail Settings', width: 130, sortable: false, filterable: false,
          renderCell: (p) => (
            <button
              onClick={(e) => { e.stopPropagation(); openMailer(p.row); }}
              style={editButtonStyle}
            >
              Edit
            </button>
          )
        },
        {
          // Not for the synthetic "Default" dealer: it holds owner-class users, who aren't dealer-scoped.
          field: 'products', headerName: 'Products', width: 110, sortable: false, filterable: false,
          renderCell: (p) => (p.row.code || '').toLowerCase() === 'default' ? null : (
            <button
              onClick={(e) => { e.stopPropagation(); openProducts(p.row); }}
              style={editButtonStyle}
            >
              Edit
            </button>
          )
        },
    ];

    const formatLastLogin = (iso) => {
        if (!iso) return '—';
        return formatDateTime(iso, tz) || '—';
    };

    const userColumns = [
        { field: 'id',         headerName: 'ID',         width: 70 },
        { field: 'username',   headerName: 'Username',   flex: 1 },
        { field: 'email',      headerName: 'Email',      flex: 1 },
        { field: 'role_name',  headerName: 'Role',       width: 150 },
        { field: 'last_login', headerName: 'Last Login', width: 160,
          renderCell: (params) => formatLastLogin(params?.row?.last_login) },
    ];

    const gridSx = {
        fontSize: '13px',
        backgroundColor: 'transparent',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden',
        '& .MuiDataGrid-virtualScroller':         { backgroundColor: 'transparent' },
        '& .MuiDataGrid-footerContainer':         { backgroundColor: 'transparent', borderTop: '1px solid #e0e0e0', color: tc.text },
        '& .MuiDataGrid-columnHeader':            { backgroundColor: tc.headerBg, color: tc.text },
        '& .MuiDataGrid-columnHeaderTitle':       { fontSize: '12.5px', fontWeight: 600 },
        '& .MuiDataGrid-cell':                    { padding: '0 10px !important', fontSize: '13px !important', color: tc.text },
        '& .MuiDataGrid-row':                     { cursor: 'pointer' },
        '& .MuiDataGrid-row:hover':               { backgroundColor: `${tc.buttonHover} !important` },
        '& .MuiDataGrid-row.Mui-selected':        { backgroundColor: `${tc.buttonBgSelected} !important` },
        '& .MuiDataGrid-row.Mui-selected:hover':  { backgroundColor: `${tc.buttonHoverActive} !important` },
        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':             { outline: 'none !important' },
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none !important' },
        '& .MuiTablePagination-root':             { color: tc.text },
    };

    return (
        <div className="content-container">
            <h3>Dealers</h3>

            <div style={{ height: 300, marginBottom: 32 }}>
                <DataGrid
                    rows={dealers}
                    columns={dealerColumns}
                    loading={loadingDealers}
                    onRowClick={handleDealerSelect}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25]}
                    disableSelectionOnClick={false}
                    rowHeight={25}
                    sx={gridSx}
                    initialState={{
                        sorting: { sortModel: [{ field: 'id', sort: 'asc' }] },
                    }}
                />
            </div>

            {brandingDealer ? (
                // "Default" dealer represents the owner-level scope -> omit dealerId
                // so LogoHeader edits the Company (owner) record directly.
                (brandingDealer.code || '').toLowerCase() === 'default' ? (
                    <LogoHeader key="owner-scope" />
                ) : (
                    <LogoHeader key={brandingDealer.id} dealerId={brandingDealer.id} dealerName={brandingDealer.name} />
                )
            ) : mailerDealer ? (
                // The synthetic "Default" dealer represents the owner-level scope,
                // so editing its mailer settings should target Site (Owner) settings
                // — i.e. omit dealerId and let MailerSettingsTab use the owner endpoint.
                (mailerDealer.code || '').toLowerCase() === 'default' ? (
                    <MailerSettingsTab key="owner-scope" />
                ) : (
                    <MailerSettingsTab key={mailerDealer.id} dealerId={mailerDealer.id} dealerName={mailerDealer.name} />
                )
            ) : productsDealer ? (
                <DealerProductsPanel key={productsDealer.id} dealerId={productsDealer.id} dealerName={productsDealer.name} />
            ) : selectedDealer && (
                <>
                    <h4>Users in {selectedDealer.name}</h4>
                    <div style={{ height: 350 }}>
                        <DataGrid
                            rows={users}
                            columns={userColumns}
                            loading={loadingUsers}
                            pageSize={10}
                            rowsPerPageOptions={[10, 25]}
                            disableSelectionOnClick
                            rowHeight={25}
                            sx={gridSx}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default Dealers;
